<template>
  <VDialogV3
    id="tender-card"
    position="center"
    :entity="localTender"
    :show-edit-button="$core.currentPartner.isSeller === false"
    :context-menu="TendersService.contextMenu"
    @edit-button:clicked="showRequisitesDialog"
    :on-context-menu-action="onContextMenuAction"
  >
    <template #badges>
      <VBadge v-if="localTender.productCode" :title="`Артикул: ${localTender.productCode}`"
              class="default ellipsis">
        Арт: {{ localTender.productCode }}
      </VBadge>
      <VBadge v-if="localTender.isPreorderNeeded === true" class="ellipsis">
        Предзаказ
      </VBadge>
      <VBadge v-if="localTender.winnersCategory?.id" class="ellipsis">
        {{ localTender.winnersCategory.name }}
      </VBadge>
      <VBadge v-if="localTender.isQtyOrderedFlexible === true" class="default">
        без обязательства выкупа фиксированного объёма
      </VBadge>

      <template v-if="localTender.isDeleted === false">
        <VBadge v-if="localTender.isFinished === true" class="warning ellipsis">Завершён</VBadge>
        <VBadge v-if="localTender.isNotStarted === true" class="default ellipsis">Торги не
          начались
        </VBadge>
        <VBadge v-if="localTender.isRunning === true" class="success ellipsis">Идут торги</VBadge>
        <VBadge
          v-if="localTender.isRefusedTenderByCurrentPartner === true"
          class="warning ellipsis refuse-reason"
          :title="localTender.participationDetailProductsByCurrentPartner[0].getRefusalReasonsStringRepresentation()"
        >
          {{
            localTender.participationDetailProductsByCurrentPartner[0].getRefusalReasonsStringRepresentation()
          }}
        </VBadge>
      </template>
    </template>

    <template #toolbar>
      <VButton
        class="isBookmarked"
        theme="link"
        :title="TendersService.getIsBookmarkedText(localTender)"
        @click="TendersService.toggleCategoryBookmark(localTender)"
      >
        <VIcon :material="TendersService.getIsBookmarkedIcon(localTender)"/>
      </VButton>
    </template>

    <header class="document-header">
      <dl>
        <dt>Потребность</dt>
        <dd
        :title=" $core.currentPartner.isSeller === true ? localTender.productCategory?.name : localTender.product?.name">
          <VLink class="ellipsis" @click="showDemandCard">
            {{
              $core.currentPartner.isSeller === true ? localTender.productCategory?.name : localTender.product?.name
            }}
          </VLink>
        </dd>

        <template v-if="$core.currentPartner.isBuyer === true">
          <dt>Покупатель</dt>
          <dd
          :title="localTender.buyerCompany?.name || 'Не назначен'">
            {{ localTender.buyerCompany?.name || 'Не назначен' }}</dd>
        </template>
        <template v-else>
          <dt>Грузополучатель</dt>
          <dd
          :title="localTender.buyerCompany?.name || 'Не назначен'">
            {{ localTender.buyerCompany?.name || 'Не назначен' }}</dd>
        </template>
      </dl>

      <dl>
        <dt>Сумма и кол-во</dt>
        <dd
          :title="`${localTender.qtyDesired.mul(priceCurrent).formatInDisplayCurrency(
                      priceCurrentCurrencyCode, localTender.startAt, ) || '--'} за ${localTender.qtyDesired?.format(localTender.unit) || '--'}`">
          {{
            localTender.qtyDesired.mul(priceCurrent).formatInDisplayCurrency(priceCurrentCurrencyCode, localTender.startAt) || '--'
          }}
          за {{ localTender.qtyDesired?.format(localTender.unit) || '--' }}
        </dd>

        <template v-if="localTender.preorderShipmentPeriod.isFilled()">
          <dt>Период поставки</dt>
          <dd
          :title="localTender.preorderShipmentPeriod.toString({prefix: 'с', middle: 'по'})">
            {{ localTender.preorderShipmentPeriod.toString({prefix: 'с', middle: 'по'}) }}
          </dd>
        </template>
        <template v-else>
          <dt>Дата поставки</dt>
          <dd
            :title="$core.utils.date.format(localTender.desiredReceiptDate) || '--'">
            {{ $core.utils.date.format(localTender.desiredReceiptDate) || '--' }}
          </dd>
        </template>
      </dl>

      <dl>
        <dt>Начало торгов</dt>
        <dd
        :title="localTender.startAt">
          {{ localTender.startAt }}
        </dd>

        <dt>Конец торгов</dt>
        <dd
        :title="localTender.endAt || '--'">
          {{ localTender.endAt || '--' }}
        </dd>
      </dl>

      <dl>
        <dt>Пункт доставки</dt>
        <dd class="ellipsis"
            :title=" localTender.buyerStore?.address || 'Не выбран'">
          {{
            localTender.buyerStore?.address || 'Не выбран'
          }}
        </dd>

        <dt>Ответственный</dt>
        <dd
        :title="localTender.responsibleBuyer?.name || 'Не назначен'">
          {{ localTender.responsibleBuyer?.name || 'Не назначен' }}
        </dd>
      </dl>

    </header>

    <header class="document-header requisites-time">
      <article v-if="$core.currentPartner.isSeller === true">
        <header>Ваша цена:</header>
        <span class="price">
          {{
            localTender.yourLastBid?.priceFullBuyerWithDeferredPayment.formatInDisplayCurrency(localTender.yourLastBid.offerCurrencyCode, localTender.startAt) || '--'
          }}
        </span>
      </article>

      <article>
        <header>Текущая цена:</header>
        <span class="price">
          {{
            priceCurrent.formatInDisplayCurrency(priceCurrentCurrencyCode, localTender.startAt) || '--'
          }}
        </span>
      </article>

      <article>
        <header :class="{'is-finished': localTender.isFinished === true}">
          <template v-if="localTender.isFinished === true">Торги окончены</template>
          <template v-else-if="localTender.isRunning === true">Окончание торгов</template>
          <template v-else>Начало торгов</template>
        </header>
        <span v-if="localTender.isFinished === false">
          {{ localTender.timeRemainingBeforeNextEvent }}
        </span>
      </article>

      <article v-if="localTender.isFinished === true">
        <header>Заказ:</header>
        <TenderOrdersCounter :tender="localTender"/>
      </article>
    </header>

    <div class="body">
      <section class="grids">
        <TenderBidsGrid
          :tender="localTender"
          @tenderBid:updated="renewTender(true)"
        />

        <TenderKindOfParticipantsGrid
          v-if="$core.currentPartner.isBuyer === true"
          title="Отказ"
          :tender="localTender"
          :kind-of-participants="localTender.nonParticipants"
          @participationDetailProduct:updated="onParticipationDetailProductUpdated"
        />

        <TenderKindOfParticipantsGrid
          v-if="$core.currentPartner.isBuyer === true"
          title="Нет ставки"
          :tender="localTender"
          :kind-of-participants="localTender.undecidedParticipants"
          @participationDetailProduct:updated="onParticipationDetailProductUpdated"
        />

        <footer v-if="$core.currentPartner.isSeller === true">
          <!--    Тендер не идёт    -->
          <div v-if="localTender.isRunning === false" class="info">
            <template v-if="localTender.isFinished === true">Торги окончены</template>
            <template v-else-if="localTender.isNotStarted === true">Торги еще не начались</template>
            <template v-else>Нельзя сделать ставку</template>
          </div>

          <!-- Ожидание разрешения -->
          <template v-else-if="localTender.needsPermission === true">
            <div v-if="localTender.hasParticipationRequestByCurrentPartner === true"
                 class="info">
              Вы уже послали запрос на получение права сделать ставку
            </div>
            <div v-else class="info">
              Чтобы участвовать, нужно
              <VLink @click="sendParticipationRequest">
                отправить заявку на участие в тендере
              </VLink>
            </div>
          </template>

          <!-- У продавца уже есть ставка — можно только понизить цену -->
          <VLink
            v-else-if="localTender.hasOwnBid === true"
            :disabled="localTender.isFinished"
            @click="showChangePriceBidPopup"
          >
            <div ref="add-offer">Предложить цену ниже</div>
          </VLink>

          <!-- Ставки ещё нет -->
          <template v-else>
            <template v-if="localTender.canParticipate === true">
              <VLink @click="showPositionAddDialog">
                <div ref="add-offer">Сделать ставку</div>
              </VLink>

              <VLink
                v-if="localTender.isEveryoneCanBid === false"
                @click="showRefuseDialog"
              >
                <template v-if="localTender.isRefusedTenderByCurrentPartner === true">
                  <span class="plane-text">Вы отказались участвовать,</span>
                  изменить причину неучастия
                </template>
                <template v-else>
                  Отказаться от участия
                </template>
              </VLink>
            </template>

            <div v-else class="info error">У вас нет прав участвовать</div>
          </template>
        </footer>
      </section>

      <div class="feed">
        <header>История тендера</header>

        <Feed
          ref="feedComponent"
          :parent-entities="tenderPointer"
          :is-public="$core.currentPartner.isSeller"
        />

        <footer v-if="$core.currentPartner.isBuyer === true">
          <VCommentCreateForm
            class="feed-footer"
            :parent-entity="tenderPointer"
            @comment:created="$refs.feedComponent?.renewData"
          />
        </footer>
      </div>
    </div>

    <template #footer>
      <VButton
        v-if="$core.currentPartner.isBuyer === true"
        theme="primary"
        :disabled="
        localTender.isNotStarted === true
         || localTender.isDeleted === true
         || $core.utils.isEmptyArray(localTender.bids)"
        @click="compareWithDemand"
      >
        {{ localTender.isRunning === true ? 'Сравнить предложения' : 'Выбрать победителя' }}
      </VButton>

      <VButton @click="$core.dialogs.TenderCard.hide()">Отмена</VButton>
    </template>
  </VDialogV3>
</template>

<style lang="scss" src="./TenderCard.scss"/>

<script lang="ts">
import TenderBidsGrid
  from "@/domains/TenderBids/components/TenderBidsGrid/TenderBidsGrid.vue";
import TenderKindOfParticipantsGrid
  from "@/domains/Tenders/components/TenderCard/TenderKindOfParticipantsGrid.vue";
import TenderOrdersCounter
  from '@/domains/Tenders/components/TenderOrdersCounter/TenderOrdersCounter.vue';
import TenderParticipationRequest from '@/domains/TenderParticipationRequest/TenderParticipationRequest';
import TenderParticipationRequestsService
  from '@/domains/TenderParticipationRequest/TenderParticipationRequestsService';
import TendersService from '@/domains/Tenders/TendersService';
import EntityPointer from '@/domains/EntityPointer/EntityPointer';
import Tender from '@/domains/Tenders/Tender';
import type Price from '@/core/types/Price';

export default defineComponent({
  name: 'TenderCard',

  components: {
    TenderBidsGrid,
    TenderKindOfParticipantsGrid,
    TenderOrdersCounter,
  },

  props: {
    id: {
      type: Number,
      required: true,
    },
  },

  emits: ['save'],

  setup() {
    return {TendersService};
  },

  data() {
    return {
      localTender: undefined as unknown as Tender,

      /** Мотивация — серверное время может отличаться от браузерного:
       *  интервалы считаем по пользовательскому времени, а в пэйлоаде
       *  отправляем то, что получили в прошлый раз с сервера. */
      serverTimeFetchedAt: null,
      userTimeSoftPolledAt: 0,
      userTimeHardPolledAt: 0,

      liveTimeSubscription: null as any,
    };
  },

  async created() {
    await this.init();
  },

  beforeUnmount() {
    this.unsubscribeTender();
  },

  computed: {
    /**
     * Текущая цена торгов: лучшая ставка, а до первой ставки — стартовая.
     * Валюту не несёт, она в priceCurrentCurrencyCode.
     */
    priceCurrent(): Price {
      if (this.localTender.bestBid === null) {
        return this.localTender.priceStartFull;
      } else {
        return this.localTender.bestBid.priceFullBuyerWithDeferredPayment;
      }
    },

    /** Валюта, в которой выражена priceCurrent. */
    priceCurrentCurrencyCode(): string {
      if (this.localTender.bestBid === null) {
        return this.localTender.priceStartCurrencyCode;
      } else {
        return this.localTender.bestBid.offerCurrencyCode;
      }
    },

    /** Указатель на тендер для формы комментария. */
    tenderPointer(): EntityPointer {
      return new EntityPointer('tender', this.localTender.id);
    },
  },

  methods: {
    async init(): Promise<void> {
      const response = await TendersService.get(this.id, null, this.$core.timeZone, false, {});

      if (!response) return;

      this.localTender = response.tender;
      this.serverTimeFetchedAt = response.lastFetchTimeStamp;

      await this.localTender.loadParticipants();
      await this.localTender.loadParticipationRequests();

      /** Начальная порция истории — отдаём в Feed. Первая порция тихая (без
       *  блинка), новизну дальше отслеживает сам Feed по своему набору
       *  показанных id. */
      this.pushFeedRecords();

      /** За удалённым или завершённым тендером следить смысла нет */
      if (this.localTender.isDeleted === true || this.localTender.isFinished === true) {
        // Подписка не нужна
      } else {
        this.subscribeTender();
      }
    },

    showRequisitesDialog(): void {
      this.$core.dialogs.TenderRequisites.show(
        {tender: this.localTender},
        {
          updated: (tender: Tender) => {
            this.localTender = tender;
          },
        },
      );
    },

    subscribeTender(): void {
      if (typeof this.liveTimeSubscription === 'function') {
        // Уже подписаны
      }
      else {
        this.userTimeSoftPolledAt = Number(this.$core.liveTime);
        this.userTimeHardPolledAt = Number(this.$core.liveTime);
        this.liveTimeSubscription = this.$core.liveTime.subscribe(this.longPollingSubscription);
      }
    },

    unsubscribeTender(): void {
      /* this.$core.liveTime.subscribe - возвращает функцию отписки, которую нужно вызвать */
      if (typeof this.liveTimeSubscription === 'function') {
        this.liveTimeSubscription();
        this.liveTimeSubscription = null;
      }
      else {
        // Подписки нет
      }
    },

    /**
     * Hard polling проверяется первым — это даёт два эффекта:
     *   1. На одном тике никогда не сработают и hard, и soft одновременно
     *      (избегаем race condition между renewTender() и renewTender(true)).
     *   2. Если ноут просыпался из спячки, между предыдущим и текущим тиком
     *      может пройти час и больше. В этом случае hard interval гарантированно
     *      превышен — выполнится полный refresh, а не дельта-fetch с устаревшим
     *      serverTimeFetchedAt.
     */
    async longPollingSubscription(currentUserTime): Promise<void> {
      if (currentUserTime - this.userTimeHardPolledAt > TendersService.config.HARD_POLL_INTERVAL) {
        this.userTimeHardPolledAt = currentUserTime;
        this.userTimeSoftPolledAt = currentUserTime;
        await this.renewTender(true);
        return;
      }

      if (currentUserTime - this.userTimeSoftPolledAt > TendersService.config.SOFT_POLL_INTERVAL) {
        this.userTimeSoftPolledAt = currentUserTime;
        await this.renewTender();
      }
    },

    /**
     * Пункт контекстного меню что-то изменил. Удалённый тендер закрываем —
     * хост получит true и обновится, остальное — перечитываем.
     */
    async onContextMenuAction(): Promise<void> {
      if (this.localTender.isDeleted === true) {
        this.$core.dialogs.TenderCard.resolve();
      }
      else {
        await this.renewTender(true);
      }
    },

    onParticipationDetailProductUpdated() {
      this.renewTender(true)
      this.$refs?.feedComponent.renewData()
    },

    async renewTender(isForce: boolean = false): Promise<void> {
      if (isForce === true) {
        this.$refs?.feedComponent.renewData()
      }

      await this.syncTender(isForce);
    },

    async syncTender(isForce: boolean): Promise<void> {
      const response = await TendersService.get(
        this.localTender.id,
        isForce === true ? null : this.serverTimeFetchedAt,
        this.$core.timeZone,
        this.localTender.isDeleted,
        // Опрос идёт раз в 5 с (SOFT_POLL_INTERVAL): зависший запрос обрываем раньше следующего тика,
        // чтобы запросы не копились, а отмена предыдущего не даёт старому ответу перетереть новый
        {timeout: 2900, abortPrevious: true},
      );

      if (!response) return;

      const {tender, lastFetchTimeStamp} = response;
      this.serverTimeFetchedAt = lastFetchTimeStamp;

      /** Отдаём свежие записи в Feed: он сам определит новые (по своему набору
       *  показанных id), пометит их для блинка и вернёт сколько добавилось. */
      const newCount = this.pushFeedRecords(tender.changes);

      /** Если форс-релоад или появились новые записи — обновляем тендер целиком
       *  (он несёт участников, ставки, заявки; их надо переподгрузить). */
      if (isForce === true || newCount > 0) {
        /** Сначала обогащаем только что пришедший тендер, потом подменяем —
         *  иначе таблица мигнёт на время запроса. */
        await tender.loadParticipants();
        await tender.loadParticipationRequests();

        this.localTender = tender;
      }
    },

    /**
     * Отдаёт записи истории в Feed (дозагрузка порцией). Feed сам метит новые
     * и мигает. Возвращает количество новых записей.
     */
    pushFeedRecords(records = this.localTender?.changes): number {
      return this.$refs?.feedComponent?.pushRecords(records ?? []) || 0;
    },

    /**
     * Попап понижения цены. Ставка клонируется внутри попапа: тендер
     * обновляется каждые несколько секунд, а ставка ссылается на него — без
     * клона вотчер перетирал бы вводимое значение на каждом обновлении.
     */
    async showChangePriceBidPopup(event: MouseEvent): void {
      this.$core.dialogs.TenderChangeBidPopup.hideIfOpen();
      const isNeedToUpdate = await this.$core.dialogs.TenderChangeBidPopup.showAsPromise(
        {
          bid: this.localTender.yourLastBid,
          bestBid: this.localTender.bestBid,
          target: event.target,
        }
      );

      if (isNeedToUpdate) {
        await this.renewTender(true)
      }
    },

    async showPositionAddDialog(): void {
      const tenderBidWasCreated = await TendersService.showOffersRegistryDialog(this.localTender)

      if (tenderBidWasCreated) this.renewTender(true);
    },

    async showRefuseDialog(): void {
      /** Отдаём участия по допущенным разрешениям: причина отказа у поставщика
       *  одна на тендер и проставляется во все записи сразу, а по исключённым
       *  из торгов разрешениям отказываться не от чего. */
      const isNeedToUpdate = await TendersService.showRefuseDialog(this.localTender.participationDetailProductsNotExcludedByCurrentPartner);

      if (isNeedToUpdate) this.renewTender(true);
    },

    async sendParticipationRequest(): Promise<void> {
      const tenderParticipationRequest = TenderParticipationRequest.fromTender(this.localTender);

      if (await this.$core.confirm('Отправить заявку на участие в тендере?')) {
        await TenderParticipationRequestsService.send(tenderParticipationRequest);
        await this.renewTender(true);
      }
    },

    async compareWithDemand(): void {
      const isNeedToUpdate = await TendersService.compareWithDemand(this.localTender)

      if (isNeedToUpdate) this.renewTender(true);
    },

    showDemandCard(): void {
      this.$core.dialogs.DemandCard.show({
        id: this.localTender.demand.id
      });

      // пользователь заходит, добавляет поставщика, выходит, надо сразу обновить лист участников
      // this.renewTender(true);
    },
  },
});
</script>